import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "./toast.scss";

let toastContainer = null;
let toastRoot = null;

const initializeToastContainer = () => {
  if (!toastContainer) {
    toastContainer = document.createElement("div");
    toastContainer.className = "pdf-toast-container";
    // Prevent clicks on toast container from propagating to document
    toastContainer.addEventListener("mousedown", (e) => {
      e.stopPropagation();
    });
    toastContainer.addEventListener("click", (e) => {
      e.stopPropagation();
    });
    document.body.appendChild(toastContainer);
    toastRoot = createRoot(toastContainer);
  }
};

const ToastItem = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleRemove = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsRemoving(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleRemove();
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const getIcon = () => {
    switch (toast.type) {
      case "success":
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm-2 15l-5-5 1.41-1.41L8 12.17l7.59-7.59L17 6l-9 9z"
              fill="currentColor"
            />
          </svg>
        );
      case "error":
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z"
              fill="currentColor"
            />
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z"
              fill="currentColor"
            />
          </svg>
        );
    }
  };

  return (
    <div
      className={`pdf-toast pdf-toast-${toast.type} ${isVisible ? "pdf-toast-visible" : ""} ${isRemoving ? "pdf-toast-removing" : ""}`}
      onClick={handleRemove}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="pdf-toast-icon">{getIcon()}</div>
      <div className="pdf-toast-message">{toast.message}</div>
      <button className="pdf-toast-close" onClick={handleRemove} onMouseDown={(e) => e.stopPropagation()}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M12 4L4 12M4 4l8 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
};

const ToastList = ({ toasts, onRemove }) => {
  return (
    <>
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </>
  );
};

let toasts = [];

const renderToasts = () => {
  if (!toastRoot) return;

  toastRoot.render(
    <ToastList
      toasts={toasts}
      onRemove={(id) => {
        toasts = toasts.filter((toast) => toast.id !== id);
        renderToasts();
      }}
    />
  );
};

export const showToast = (message, type = "info") => {
  initializeToastContainer();

  const id = Date.now() + Math.random();
  const newToast = { id, message, type };

  toasts = [...toasts, newToast];
  renderToasts();

  // Auto remove after 1.5 seconds
  setTimeout(() => {
    toasts = toasts.filter((toast) => toast.id !== id);
    renderToasts();
  }, 1500);
};

// Hook for compatibility
export const useToast = () => {
  return showToast;
};

